/**
 * Shared types for the Carbon primitives (components/carbon). Wave 2 slices
 * import from here (via `@/components/carbon`), never from a primitive file.
 * Additive only after Wave 1: renaming or narrowing a type here breaks a
 * frozen contract.
 */
import type { ButtonHTMLAttributes, HTMLAttributes, MouseEventHandler, ReactNode } from 'react'
import type { ActionBrief } from '@/lib/brief-shape'

/** A categorical data color (DESIGN.md "Secondary"). Marks only, never a state. */
export type Category = 'baseline' | 'bpa' | 'identity' | 'secure'

/** A Guardian pillar as the brief composers and PostureBrief frames name it. */
export type PillarTone = 'compliance' | 'bpa' | 'identity'

/** The category mark each pillar draws with (compliance is the Baseline pillar). */
export const TONE_CATEGORY: Record<PillarTone, Category> = {
  compliance: 'baseline',
  bpa: 'bpa',
  identity: 'identity',
}

/**
 * Severity of a priority action. 'routine' mirrors PriorityAction.severity in
 * lib/posture-brief.ts so action rows render without a coercion step; it
 * reads as 'low'.
 */
export type ActionSeverity = 'crit' | 'high' | 'med' | 'low' | 'routine'

/** The four severity shapes: octagon, triangle, diamond, square (Shape-First Severity Rule). */
export type SeverityLevel = 'crit' | 'high' | 'med' | 'low'

export type ButtonKind = 'primary' | 'secondary' | 'tertiary' | 'ghost'
/** lg 48px (Carbon's default), md 40px, sm 32px. */
export type ButtonSize = 'sm' | 'md' | 'lg'

export type TagKind = 'default' | 'cat' | 'high-contrast' | 'outline'
/** md 24px, sm 18px (the count tag inside a tab). */
export type TagSize = 'md' | 'sm'

/** StatusDot tones: neutral (idle), active (strong ink), crit (Coral). */
export type StatusTone = 'neutral' | 'active' | 'crit'

/** Field heights: sm 32px, md 40px (Carbon's default), lg 48px (toolbar fields). */
export type FieldSize = 'sm' | 'md' | 'lg'

/**
 * A meter or bar segment's fill: a severity (Coral for crit only), the strong neutral (`strong`,
 * members without MFA), the unearned-point gray (`off`) or a pillar's category mark.
 */
export type MeterTone = SeverityLevel | 'strong' | 'off' | Category

// ---------------------------------------------------------------------------
// Lane A primitives' types, hoisted here at W1.6 (blueprint §4.0). Each
// primitive file imports its props from this module.
// ---------------------------------------------------------------------------

// ---- AppHeader
export interface AppHeaderProps {
  /** Toggles the phone side nav. */
  onMenu: () => void
  menuOpen: boolean
  /** "Last assessed 06:12"; hidden below 1056px. */
  meta?: ReactNode
  /** The 48px header buttons, right-aligned. */
  children?: ReactNode
  /** The wordmark's route. */
  homeTo?: string
  /** id of the side nav the menu button controls. */
  navId?: string
}

// ---- AppShell
export interface AppShellProps {
  /** The AppHeader. */
  header: ReactNode
  /** The SideNav. */
  nav: ReactNode
  /** The phone drawer is open. */
  navOpen: boolean
  onCloseNav: () => void
  /** id of the side nav: focus moves into it on open and back to its `aria-controls` button on close. */
  navId?: string
  /** id of the <main> the skip link targets. */
  mainId?: string
  /** The <main> element. */
  children: ReactNode
}

// ---- Avatar
export interface AvatarProps {
  /** One or two letters. */
  initials: string
  tone: 'tenant' | 'user'
  size?: 24 | 32 | 40
  /** Accessible name when the avatar stands alone. */
  label?: string
  className?: string
}

// ---- BigNumber
export type BigNumberSize = 'score' | 'figure-lg' | 'figure'

export interface BigNumberProps {
  value: number | string | null | undefined
  /** Renders "/ {of}" after the number (score tiles). */
  of?: number
  size?: BigNumberSize
  /** A trailing unit ("%"), set like the "/ 100". */
  suffix?: string
  as?: 'p' | 'div' | 'span'
  className?: string
}

// ---- Breadcrumb
export interface BreadcrumbItem {
  label: string
  /** Parent route; the last item is the current page and never links. */
  to?: string
}

export interface BreadcrumbProps {
  items: ReadonlyArray<BreadcrumbItem>
  className?: string
}

// ---- BriefHero
/** A button primary for a brief whose action is not a route: a mutation (start an audit) or a download. */
export interface BriefHeroButtonAction {
  label: ReactNode
  onClick: () => void
  /** Busy: a spinner in place of the icon, the button disabled and aria-busy. */
  loading?: boolean
  disabled?: boolean
  /** Trailing icon. Default the arrow, like the brief's link primary. */
  icon?: ReactNode
  /** The accessible name when the label alone is not enough ("Download executive report as PDF"). */
  'aria-label'?: string
}

export interface BriefHeroProps {
  /** An ActionBrief or any composer's output (normalized by toActionBrief). */
  brief: ActionBrief | object
  /** A button primary in place of the brief's link `primaryAction` (which composers emit as a route). */
  primaryAction?: BriefHeroButtonAction
  /** The tertiary beside the primary action (normally a `ButtonLink kind="tertiary"`). */
  secondaryAction?: ReactNode
  /**
   * A view control at the end of the actions row (the Executive Report's period Select). Unlike the
   * actions, it stays while the brief loads, so the view can still be changed.
   */
  controls?: ReactNode
  /** The provenance line ("Assessed by Guardian at 06:12 · 186 settings checked"). */
  provenance?: ReactNode
  /**
   * hero: the Command Center (3.375rem, 18ch); pillar: pillar pages (3rem, 22ch); tile: the brief inside a tile
   * (1.25rem/400 headline, body lede, no outer padding), under the tile's own header.
   */
  size?: 'hero' | 'pillar' | 'tile'
  /** id of the heading, for an aria-labelledby on the surrounding section. */
  headingId?: string
  /**
   * The verdict's element: h1 (default, the page's verdict) or h2 for a brief
   * under the page's own h1 (a first-run brief, a brief inside a tile). Same look.
   */
  headingLevel?: 'h1' | 'h2' | 'h3'
  /** While the composer has nothing to say yet: an announced, empty h1 and no lede. */
  isLoading?: boolean
  className?: string
}

// ---- CondensedGrid
export type GridColumnCount = 1 | 2 | 3 | 4 | 5 | 6

export interface CondensedGridProps extends HTMLAttributes<HTMLElement> {
  columns?: { base: GridColumnCount; 'c-md'?: GridColumnCount; 'c-lg'?: GridColumnCount; 'c-xlg'?: GridColumnCount }
  /** `ol` for tiles in a sequence (numbered steps); the list markers stay off. */
  as?: 'div' | 'section' | 'ul' | 'ol'
  children: ReactNode
}

// ---- HeaderButton
export interface HeaderButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label'> {
  /** Accessible name ("Notifications, 2 new", "Switch to light theme"). */
  label: string
  /** The icon, drawn at 20px. */
  icon: ReactNode
  /** A NotificationBadge, positioned inside the button. */
  badge?: ReactNode
  /** Open / pressed look (a popover that is open). */
  active?: boolean
  /** Renders a router link to this route instead of a button. */
  to?: string
}

// ---- InlineNotification
export interface InlineNotificationProps {
  kind: 'error' | 'info'
  title: ReactNode
  /** The one-line body after the title. */
  children?: ReactNode
  /** Trailing action (a link or a small button). */
  actions?: ReactNode
  onClose?: () => void
  /** The dismiss button's accessible name. */
  closeLabel?: string
  className?: string
}

// ---- LineTabs
export interface LineTabItem {
  id: string
  label: string
  /** Link mode: the route this tab opens. */
  to?: string
  /** A count tag after the label (TabConfig.badge). null or '' hides it. */
  count?: number | string | null
  disabled?: boolean
}

export interface LineTabsProps {
  items: ReadonlyArray<LineTabItem>
  /** The selected item's id. Required: selection never comes from the router. */
  value: string
  /** Tab mode: called with the chosen id (click or arrow keys). Link mode: called on click. */
  onChange?: (id: string) => void
  /** Beside the strip; on its own row below 672px. */
  actions?: ReactNode
  'aria-label': string
  /** Tab mode: id prefix of each tab. */
  tabIdPrefix?: string
  /** Tab mode: id prefix of the panel each tab controls. */
  panelIdPrefix?: string
  className?: string
}

// ---- MicrosoftSignInButton
export interface MicrosoftSignInButtonProps {
  onClick?: MouseEventHandler<HTMLButtonElement>
  /** Sign-in in progress: spinner, busy label, no further clicks. */
  busy?: boolean
  label?: string
  disabled?: boolean
  type?: 'button' | 'submit'
  className?: string
}

// ---- NotificationBadge
export interface NotificationBadgeProps {
  count: number
  max?: number
}

// ---- PhoneMenu
export interface PhoneMenuProps {
  open: boolean
  onToggle: () => void
  /** id of the side nav it controls. */
  controls?: string
}

// ---- ScoreTile
export interface ScoreTileProps {
  title: ReactNode
  /** Leads the title with this pillar's swatch. */
  category?: Category
  /** 0..of; null renders a dash. */
  score: number | null
  of?: number
  /** A unit after the numeral ("%") in place of "/ {of}" (Secure Score, an audit pass rate). */
  suffix?: string
  /** The delta, normally a default `Tag` ("+7 in 12 weeks"). */
  delta?: ReactNode
  /** Helper text beside the delta ("Weighted by severity"). */
  meta?: ReactNode
  /** Six words or fewer; rendered on the hero tile only. */
  verdict?: string
  /** Right column of the hero tile (the waffle and its key). */
  aside?: ReactNode
  /** Pillar tile: a visualization under the numeral and meta row (the 30-point PointGrid, a SegmentedMeter). */
  visual?: ReactNode
  size?: 'pillar' | 'hero'
  /**
   * The tile's element, default `div`. `section` and `aside` are landmarks named by the title, so pass `headingId`
   * with them.
   */
  as?: 'div' | 'section' | 'aside'
  /** Heading level of the title. */
  level?: 2 | 3
  headingId?: string
  isLoading?: boolean
  className?: string
}

// ---- SideNav
export interface NavItemCount {
  n: number
  tone: 'neutral' | 'crit'
  /** Read after the label ("6 open drifts"). */
  aria: string
}

export interface NavItemSpec {
  to: string
  label: string
  /** The leading mark: a Swatch, the IuvoMark or a 16px icon. */
  lead: ReactNode
  count?: NavItemCount
  /** Match the route exactly (default true). */
  end?: boolean
}

export interface SideNavGroup {
  label: string
  items: ReadonlyArray<NavItemSpec>
}

export interface SideNavUser {
  name: string
  /** The Guardian role, humanized ("Global Admin"). */
  role?: string
  initials: string
}

export interface SideNavUserAction {
  id: string
  label: string
  icon?: ReactNode
  onClick?: () => void
  /** In-app route. */
  to?: string
  /** External URL, opened in a new tab. */
  href?: string
}

export interface SideNavProps {
  groups: ReadonlyArray<SideNavGroup>
  /** The TenantSwitcher. */
  tenantSlot?: ReactNode
  user?: SideNavUser | null
  /** The user menu's entries. */
  userActions?: ReadonlyArray<SideNavUserAction>
  /** The phone drawer is open. */
  open: boolean
  /** Closes the phone drawer (called on navigation). */
  onClose: () => void
  id?: string
}

// ---- StructuredList
export interface StructuredListRow {
  id: string
  /** The time column ("Tue 14:20"). */
  time?: ReactNode
  title: ReactNode
  /** Helper line under the title ("New drift · SharePoint"). */
  sub?: ReactNode
  /** Trailing content, normally a `Tag`. */
  tag?: ReactNode
  /** Makes the title a router link. */
  href?: string
}

export interface StructuredListProps {
  rows: ReadonlyArray<StructuredListRow>
  /** Width of the time column in px; 0 drops the column. */
  timeColumn?: 112 | 0
  'aria-label'?: string
  /** Shown instead of the rows when there are none. */
  empty?: ReactNode
  className?: string
}

// ---- TenantSwitcher
export interface TenantOption {
  id: string
  name: string
  domain?: string
}

export interface TenantSwitcherProps {
  tenants: ReadonlyArray<TenantOption>
  /** The selected tenant; undefined is "All tenants" when `allTenantsLabel` is set. */
  currentId: string | undefined
  onSelect: (id: string | undefined) => void
  isLoading?: boolean
  /** Offers an all-tenants row (global admins) with this label. */
  allTenantsLabel?: string
  /** False renders the current tenant as a static block (one tenant, no choice). */
  switchable?: boolean
}

// ---- Tile
/** `aside` for a tile beside the brief that complements it (a score or inventory tile), with an accessible name. */
export type TileElement = 'div' | 'section' | 'article' | 'li' | 'aside'

export interface TileProps extends HTMLAttributes<HTMLElement> {
  /** The element to render when the tile is not a link. */
  as?: TileElement
  padding?: 0 | 16 | 24
  /** Clickable tile: the whole tile is a link to this route. */
  to?: string
  children: ReactNode
}

// ---- TileHeader
export interface TileHeaderProps {
  title: ReactNode
  /** Leads with this pillar's swatch. */
  category?: Category
  /** Leading mark when the tile is not a pillar (the iuvo mark, an icon). Ignored with `category`. */
  lead?: ReactNode
  /** Heading level of the title. */
  level?: 2 | 3 | 4
  id?: string
  /** Trailing content, pushed to the far edge (a tag, an icon button). */
  aside?: ReactNode
  className?: string
}

// ---- Tooltip
export interface TooltipProps {
  /** The tooltip text. */
  label: ReactNode
  /** The trigger content. */
  children: ReactNode
  align?: 'start' | 'center' | 'end'
  side?: 'top' | 'bottom'
  interactive?: boolean
  /** Carbon's definition tooltip: a dotted underline on the trigger. */
  definition?: boolean
  className?: string
}
